import React, { useState } from 'react';
import { Bug, AlertCircle, Send } from 'lucide-react';
import { motion } from 'framer-motion';

const ReportBug = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '',
    email: '',
    severity: '',
    steps: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setFormData({
      title: '',
      description: '',
      type: '',
      email: '',
      severity: '',
      steps: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-slate-900 to-purple-900">
      <div className="px-4 pb-12 pt-8">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Bug className="w-8 h-8 text-red-400" />
              <h1 className="text-3xl md:text-4xl font-bold text-white">Report an Issue</h1>
            </div>
            <p className="text-lg text-white/70">
              Help us improve by reporting any bugs or issues you encounter
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-slate-800/40 to-slate-800/20 backdrop-blur-xl 
                      rounded-2xl border border-white/5 p-6 md:p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Issue Title *
                  </label>
                  <input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Brief description of the issue"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg
                             text-white placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                             outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Type *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg
                               text-white placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                               outline-none"
                      required
                    >
                      <option value="">Select issue type</option>
                      <option value="bug">Bug</option>
                      <option value="feature">Feature Request</option>
                      <option value="ui">UI/UX Issue</option>
                      <option value="performance">Performance Issue</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Severity *
                    </label>
                    <select
                      name="severity"
                      value={formData.severity}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg
                               text-white placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                               outline-none"
                      required
                    >
                      <option value="">Select severity</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Steps to Reproduce *
                  </label>
                  <textarea
                    name="steps"
                    value={formData.steps}
                    onChange={handleChange}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. Notice that..."
                    rows={4}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg
                             text-white placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                             outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Detailed Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Please provide as much detail as possible about the issue..."
                    rows={6}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg
                             text-white placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                             outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Your Email (optional)
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="To receive updates about this issue"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg
                             text-white placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                             outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center pt-2">
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>Your report will be reviewed by our team</span>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg
                           flex items-center gap-2 transition-colors duration-200 disabled:opacity-50
                           disabled:cursor-not-allowed"
                >
                  <span>{isSubmitting ? 'Submitting...' : 'Submit Report'}</span>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ReportBug;